import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { canAccessSection, landingPath, sectionKeyForPath } from './sections'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { public: true, layout: 'blank' },
  },
  {
    path: '/',
    component: () => import('./components/AppLayout.vue'),
    children: [
      { path: '', name: 'dashboard', component: () => import('./views/DashboardView.vue') },
      // Пагинированные разделы («Клиенты», «Платежи», вкладки «Тарифы» /
      // «eSIM» / «Поддержка») включают fixed-режим контентной области
      // сами — через useFixedHeightLayout() (см. composables/useFixedLayout.ts).
      { path: 'users', name: 'users', component: () => import('./views/UsersView.vue') },
      { path: 'users/:id', name: 'user-detail', component: () => import('./views/UserDetailView.vue'), props: true },
      { path: 'acquirers', name: 'acquirers', component: () => import('./views/AcquirersView.vue') },
      { path: 'providers', name: 'providers', component: () => import('./views/ProvidersView.vue') },
      { path: 'plans', name: 'plans', component: () => import('./views/PlansView.vue') },
      // Раздел «Маркетплейсы»: список площадок и карточка одной площадки
      // с кабинетами и выложенными тарифами (см.
      // api/admin/routers/marketplaces.py, marketplace/USAGE.md).
      {
        path: 'marketplaces',
        name: 'marketplaces',
        component: () => import('./views/MarketplacesView.vue'),
      },
      // Редактор фото-карточек — макеты изображения товара, которые
      // площадка получает уже подставленными под конкретный тариф
      // (см. api/admin/routers/photo_cards.py, photocard/USAGE.md).
      // Стоит ВЫШЕ 'marketplaces/:code', иначе 'photo-cards' уедет в
      // :code и откроется карточка несуществующей площадки.
      {
        path: 'marketplaces/photo-cards',
        name: 'photo-cards',
        component: () => import('./views/PhotoCardsView.vue'),
      },
      {
        path: 'marketplaces/photo-cards/:id',
        name: 'photo-card-editor',
        component: () => import('./views/PhotoCardEditorView.vue'),
        props: true,
      },
      // Редактор инструкций — текст, который покупатель цифрового товара
      // получает от площадки вместе с кодом активации: письмо, описание
      // товара, сообщение в чат (см.
      // api/admin/routers/marketplace_instructions.py, marketplace/USAGE.md).
      // Тоже ВЫШЕ 'marketplaces/:code' — по той же причине, что фото-карточки.
      {
        path: 'marketplaces/instructions',
        name: 'instructions',
        component: () => import('./views/InstructionsView.vue'),
      },
      {
        path: 'marketplaces/instructions/:id',
        name: 'instruction-editor',
        component: () => import('./views/InstructionEditorView.vue'),
        props: true,
      },
      {
        path: 'marketplaces/:code',
        name: 'marketplace-detail',
        component: () => import('./views/MarketplaceDetailView.vue'),
        props: true,
      },
      { path: 'discounts', name: 'discounts', component: () => import('./views/DiscountsView.vue') },
      { path: 'payments', name: 'payments', component: () => import('./views/PaymentsView.vue') },
      // Раздел «Финансы»: распределение чистой выручки по партнёрам,
      // операционные расходы и ежедневный финансовый отчёт (см.
      // api/admin/routers/finance.py).
      { path: 'finance', name: 'finance', component: () => import('./views/FinanceView.vue') },
      // «Партнёрская программа»: внешние партнёры с реферальными
      // ссылками — анкеты, начисления, заявки на вывод (см.
      // api/admin/routers/partner_program.py). Не путать с «Финансы →
      // Партнёры» выше. Карточка партнёра — ?partner=<user_id> в query.
      {
        path: 'partner-program',
        name: 'partner-program',
        component: () => import('./views/PartnerProgramView.vue'),
      },
      // Раздел «Отчёты»: конструктор выгрузок по eSIM, тарифам, платежам
      // и промокодам. Подраздел — это ?kind= в query, страница одна:
      // форму целиком рисует ReportBuilder по мете с сервера.
      { path: 'reports', name: 'reports', component: () => import('./views/ReportsView.vue') },
      { path: 'esims', name: 'esims', component: () => import('./views/ESimsView.vue') },
      { path: 'api-client', name: 'api-client', component: () => import('./views/ApiClientView.vue') },
      { path: 'admins', name: 'admins', component: () => import('./views/AdminsView.vue') },
      { path: 'audit', name: 'audit', component: () => import('./views/AuditView.vue') },
      { path: 'smtp', name: 'smtp', component: () => import('./views/SmtpView.vue') },
      { path: 'sms', name: 'sms', component: () => import('./views/SmsView.vue') },
      { path: 'telegram', name: 'telegram', component: () => import('./views/TelegramView.vue') },
      // Раздел «Push»: Firebase Cloud Messaging — состояние ключа, тексты
      // и тумблеры сценариев, тестовая отправка, рассылка, устройства
      // клиентов и история (см. api/admin/routers/push.py).
      { path: 'push', name: 'push', component: () => import('./views/PushView.vue') },
      { path: 'cache', name: 'cache', component: () => import('./views/CacheView.vue') },
      // Раздел «Тест»: диагностические инструменты, запускаемые с сервера
      // (см. api/admin/routers/test.py). Сейчас — подбор формата e-mail
      // при выпуске eSIM у SmartCard.
      { path: 'test', name: 'test', component: () => import('./views/TestView.vue') },
      { path: 'settings', name: 'settings', component: () => import('./views/SettingsView.vue') },
      // Раздел «Рассылка»: подписчики клиентского бота + задания на
      // маркетинговые рассылки. Доступ по admin-JWT, как и остальные.
      {
        path: 'broadcasts',
        name: 'broadcasts',
        component: () => import('./views/BroadcastsView.vue'),
      },
      // Раздел «Поддержка»: read-mostly список Jivo-диалогов и команд
      // операторов (см. api/admin/routers/support.py).
      {
        path: 'support',
        name: 'support',
        component: () => import('./views/SupportView.vue'),
      },
      {
        path: 'support/:chatId',
        name: 'support-chat',
        component: () => import('./views/SupportChatView.vue'),
        props: true,
      },
      // Группа «SMS» — одноразовые номера для ПРИЁМА чужих кодов
      // (grizzlysms и будущие агрегаторы). С разделом 'sms' выше,
      // который про исходящие уведомления, общего ничего не имеет
      // (см. api/admin/routers/sms_senders.py, sms_sender_api/USAGE.md).
      {
        path: 'sms-providers',
        name: 'sms-providers',
        component: () => import('./views/SmsSendersView.vue'),
      },
      {
        path: 'sms-countries',
        name: 'sms-countries',
        component: () => import('./views/SmsCountriesView.vue'),
      },
      {
        path: 'sms-services',
        name: 'sms-services',
        component: () => import('./views/SmsServicesView.vue'),
      },
      {
        path: 'proxies',
        name: 'proxies',
        component: () => import('./views/ProxiesView.vue'),
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // Если ещё не пробовали — попытаемся залогиниться через Telegram WebApp
  // initData. Также подгружаем профиль (auth.admin), если есть токен, но
  // профиль ещё не загружен — он нужен для проверки раздел-уровневого
  // доступа ниже (иначе при заходе по прямой ссылке после релоада
  // редирект не сработает до первого ответа API).
  if (!auth.token || !auth.admin) {
    await auth.tryRestore()
  }
  if (!to.meta.public && !auth.token) {
    return { name: 'login', query: { next: to.fullPath } }
  }
  if (to.name === 'login' && auth.token) {
    return landingPath(auth.admin)
  }
  // Раздел-уровневый доступ: обычный админ с ограниченным
  // allowed_sections не может зайти на чужой раздел даже по прямой
  // ссылке. Бэкенд всё равно отдаст 403, но мягкий редирект приятнее.
  // Уводим на первый доступный раздел: дашборд тоже может быть не выдан.
  if (!to.meta.public && auth.admin) {
    const section = sectionKeyForPath(to.path)
    if (section && !canAccessSection(auth.admin, section)) {
      const target = landingPath(auth.admin)
      if (target !== to.path) return target
    }
  }
})

export default router
